import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import {
    registerBeteiligteAbrufen,
    registerBeteiligteVorschau,
    registerStandLaden,
    type AbgelegterAbdruck,
    type RegisterBeteiligteErgebnis,
    type RegisterStand,
    type UebernahmeEintrag,
} from "../services/firmendatenService";
import { RegisterBeteiligteDialog } from "./redesign/RegisterBeteiligteDialog";
import { RegisterhistorieDialog } from "./redesign/RegisterhistorieDialog";
import { hinweisen } from "./redesign/bestaetigen";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { apiUrl } from "../utils/api";
import { AnsichtsKnopf, imGesperrtenFormular } from "./forms/AnsichtsKnopf";
import { alleDokumente } from "./windowManager/browserFenster/fensterRegister";

/**
 * Die drei Knöpfe unter der Handelsregisternummer (User 01.10.2026):
 *
 *  - **Personen aus dem Register** — Organe und Gesellschafter vorschlagen.
 *    In der Akte holt derselbe Portalvorgang den aktuellen Abdruck mit und
 *    legt ihn ab.
 *  - **Aktuellen Abdruck holen / Aktueller Abdruck** — noch keiner da: holen
 *    (AD + Personen in EINEM Abruf), danach das Personen-Popup. Schon da:
 *    Mini-Popover „Öffnen" (ohne Portal) / „Neu herunterladen".
 *  - **Chronologischen Abdruck holen / Chronologischer Abdruck** — dasselbe;
 *    „Öffnen" wertet den abgelegten CD aus (Gründungsdatum, Rechtsvorgänger),
 *    ohne neu abzurufen.
 *
 * **Das Portal deckelt auf 40 Abrufe je Stunde — für alle gemeinsam.** Deshalb
 * wird hier nie automatisch abgerufen: Beim Laden fragt die Maske nur
 * `register-stand` (Datenbank), abgerufen wird ausschließlich auf Klick.
 *
 * **Die Abrufe lesen Nummer und Gericht aus der Datenbank**, nicht aus der
 * Maske. Weicht die Maske vom gespeicherten Stand ab, sind die Knöpfe
 * gesperrt („Erst speichern") — sonst käme der Abdruck einer anderen Firma.
 *
 * **Neuanlage (noch keine Id):** „Personen aus dem Register" läuft über die
 * Vorschau und merkt vor; die beiden Abdruck-Knöpfe gibt es erst nach dem
 * Speichern. Wurden Personen vorgemerkt, legt das Formular nach dem Speichern
 * den aktuellen Abdruck ab (ein Abruf).
 *
 * **Schloss zu (Akte gesperrt):** „Öffnen" bleibt bedienbar — es liest nur
 * (Viewer bzw. Auswertung des abgelegten Abdrucks, ohne Übernehmen). Deshalb
 * sind die Abdruck-Knöpfe und „Öffnen" `AnsichtsKnopf`e (span role=button),
 * die ein `<fieldset disabled>` nicht abschaltet. Alles, was das Portal fragt
 * oder etwas vormerkt, bleibt gesperrt.
 *
 * Keine Hinweistexte unter der Nummer — Gründe für gesperrte Knöpfe stehen im
 * Tooltip, Fehler und Ergebnisse im Popup.
 */

const useStyles = makeStyles({
    leiste: { display: "flex", alignItems: "center", ...shorthands.gap("8px"), flexWrap: "wrap" },
    anker: { position: "relative", display: "inline-flex" },
    knopf: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        ...shorthands.padding("9px", "14px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        backgroundColor: "#fff",
        color: "#7d2169",
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "12.5px",
        fontWeight: 700,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transitionDuration: "130ms",
        ":hover": { ...shorthands.borderColor("#cd8fbd"), backgroundColor: "#f8eef5" },
        ":disabled": { color: "#a596a1", cursor: "default", backgroundColor: "#f6f3f5" },
    },
    knopfAus: { color: "#a596a1", cursor: "default", backgroundColor: "#f6f3f5", ":hover": { ...shorthands.borderColor("#ddd3dc"), backgroundColor: "#f6f3f5" } },
    menuePunktAus: { color: "#a596a1", cursor: "default", ":hover": { backgroundColor: "transparent", color: "#a596a1" } },
    knopfOffen: { ...shorthands.borderColor("#7d2169"), backgroundColor: "#f8eef5" },
    pfeil: { fontSize: "10px", marginLeft: "-2px", opacity: 0.8 },
    menue: {
        position: "absolute",
        top: "calc(100% + 6px)",
        left: 0,
        zIndex: 30,
        minWidth: "200px",
        display: "flex",
        flexDirection: "column",
        ...shorthands.padding("6px"),
        ...shorthands.borderRadius("10px"),
        ...shorthands.border("1px", "solid", "#ddd3dc"),
        backgroundColor: "#fff",
        boxShadow: "0 10px 28px rgba(36, 21, 34, 0.14)",
    },
    menuePunkt: {
        display: "block",
        width: "100%",
        textAlign: "left",
        ...shorthands.padding("9px", "11px"),
        ...shorthands.borderRadius("7px"),
        ...shorthands.borderWidth("0"),
        backgroundColor: "transparent",
        color: "#241522",
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "13px",
        fontWeight: 600,
        cursor: "pointer",
        ":hover": { backgroundColor: "#f8eef5", color: "#7d2169" },
    },
    menueStand: {
        ...shorthands.padding("4px", "11px", "6px"),
        fontSize: "11.5px",
        color: "#8b7a86",
    },
});

type Knopf = "personen" | "ad" | "cd";

const norm = (s: string | null | undefined) => String(s ?? "").replace(/\s+/g, " ").trim().toLowerCase();

const SCHLOSS = "Zum Bearbeiten oben auf das Schloss klicken";

const tagVon = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("de-DE") : null);

/** Der Viewer ist schwer (pdfjs) — erst laden, wenn wirklich geöffnet wird. */
const DocumentViewerContent = React.lazy(() =>
    import("./documents/DocumentViewerContent").then((m) => ({ default: m.DocumentViewerContent })),
);

export function HandelsregisterKnoepfe({
    contactId,
    nummer,
    registergericht,
    eintraege,
    onEintraege,
    onFelder,
    standVersion = 0,
}: {
    /** Gespeicherte Firma; `null` in der Neuanlage. */
    contactId: number | null;
    nummer: string;
    registergericht: string;
    /** Vorgemerkte Personen — übernommen wird beim Speichern (Formular/Nachlauf). */
    eintraege: UebernahmeEintrag[];
    onEintraege: (eintraege: UebernahmeEintrag[]) => void;
    /** Vorschläge aus dem chronologischen Abdruck in die Maske schreiben. */
    onFelder: (werte: Array<{ feld: string; wert: string }>) => void;
    /** Hochzählen, wenn gespeichert wurde — dann den Stand neu lesen. */
    standVersion?: number;
}) {
    const s = useStyles();
    // Fensterverwaltung, wenn es eine gibt (App-Shell). Bewusst ohne harte
    // Abhängigkeit: Die Maske wird auch außerhalb der Shell und in Tests mit
    // knapp gemockter Fensterverwaltung gerendert — dann öffnet ein neuer Tab.
    let fenster: ReturnType<typeof useWindowManager> | null = null;
    try {
        fenster = useWindowManager();
    } catch {
        fenster = null;
    }
    const [stand, setStand] = React.useState<RegisterStand | null>(null);
    const [standFehler, setStandFehler] = React.useState(false);
    const [laeuft, setLaeuft] = React.useState<Knopf | null>(null);
    const [menue, setMenue] = React.useState<"ad" | "cd" | null>(null);
    const [personen, setPersonen] = React.useState<RegisterBeteiligteErgebnis | null>(null);
    const [historie, setHistorie] = React.useState<{ documentId?: number } | null>(null);
    const leisteRef = React.useRef<HTMLDivElement | null>(null);
    // Liegt die Leiste in der gesperrten Akte (`<fieldset disabled>`)? Aus dem
    // DOM gelesen — das Schloss gehört der Akte, nicht dem Formular.
    const [gesperrt, setGesperrt] = React.useState(false);
    React.useLayoutEffect(() => {
        const jetzt = imGesperrtenFormular(leisteRef.current);
        if (jetzt !== gesperrt) setGesperrt(jetzt);
    });

    const ladeStand = React.useCallback(async () => {
        if (!contactId) return;
        try {
            setStand(await registerStandLaden(contactId));
            setStandFehler(false);
        } catch {
            // Ohne Stand bleibt „… holen" bedienbar — der Abruf selbst prüft
            // Nummer und Gericht und meldet sich verständlich.
            setStandFehler(true);
        }
    }, [contactId]);

    React.useEffect(() => {
        void ladeStand();
    }, [ladeStand, standVersion]);

    // Popover: Klick daneben oder Escape schließt.
    React.useEffect(() => {
        if (!menue) return;
        const weg = (e: MouseEvent) => {
            if (!leisteRef.current?.contains(e.target as Node)) setMenue(null);
        };
        const taste = (e: KeyboardEvent) => {
            if (e.key === "Escape") setMenue(null);
        };
        alleDokumente.addEventListener("mousedown", weg);
        alleDokumente.addEventListener("keydown", taste);
        return () => {
            alleDokumente.removeEventListener("mousedown", weg);
            alleDokumente.removeEventListener("keydown", taste);
        };
    }, [menue]);

    const bereit = Boolean(nummer.trim() && registergericht.trim());
    const ungespeichert =
        !!contactId &&
        !!stand &&
        (norm(stand.nummer) !== norm(nummer) || norm(stand.registergericht) !== norm(registergericht));

    /** Warum ein Knopf gesperrt ist — `null` = bedienbar. */
    const sperrgrund = (k: Knopf): string | null => {
        if (!bereit) return "Erst Registernummer und Registergericht eintragen";
        if (!contactId && k !== "personen") return "Nach dem Speichern verfügbar";
        if (contactId && !stand && !standFehler) return "Wird geladen …";
        if (gesperrt) return SCHLOSS;
        if (ungespeichert) return "Erst speichern — Registernummer oder Registergericht wurden geändert";
        if (laeuft) return "Register wird gelesen …";
        return null;
    };

    /** Nur lesen („Öffnen"): geht auch bei geschlossenem Schloss. */
    const sperrgrundAnsicht = (): string | null => {
        if (ungespeichert) return "Erst speichern — Registernummer oder Registergericht wurden geändert";
        if (laeuft) return "Register wird gelesen …";
        return null;
    };

    const fehlerMelden = (titel: string, e: unknown) =>
        hinweisen({ titel, text: e instanceof Error ? e.message : String(e), ton: "danger" });

    const oeffnen = (doc: AbgelegterAbdruck) => {
        setMenue(null);
        if (fenster?.openWindow) {
            const wm = fenster;
            const id = `document-viewer-${doc.documentId}`;
            wm.openWindow({
                id,
                title: doc.fileName,
                initialMaximized: true,
                render: () => (
                    <React.Suspense fallback={<div className="empty">Dokument wird geladen …</div>}>
                        <DocumentViewerContent
                            docId={doc.documentId}
                            fileName={doc.fileName}
                            mimeType={doc.mimeType}
                            onRenamed={(name) => wm.setWindowTitle?.(id, name)}
                        />
                    </React.Suspense>
                ),
            });
            return;
        }
        // Ohne Fensterverwaltung (außerhalb der App-Shell): neuer Tab.
        void (async () => {
            try {
                const res = await fetch(apiUrl(`documents/${doc.documentId}/download`));
                if (!res.ok) throw new Error(`Das Dokument ließ sich nicht öffnen (HTTP ${res.status}).`);
                const url = URL.createObjectURL(await res.blob());
                if (window.open(url, "_blank", "noopener,noreferrer")) {
                    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
                }
            } catch (e) {
                await fehlerMelden("Abdruck nicht geöffnet", e);
            }
        })();
    };

    /**
     * Personen aus dem Register holen. In der Akte legt derselbe Portalvorgang
     * den aktuellen Abdruck ab (ein Abdruck von heute wird wiederverwendet —
     * keine Dublette); in der Neuanlage nur die Vorschau.
     */
    const personenHolen = async (knopf: Knopf) => {
        setMenue(null);
        setLaeuft(knopf);
        try {
            const d = contactId
                ? await registerBeteiligteAbrufen(contactId)
                : await registerBeteiligteVorschau({
                      nummer: nummer.trim(),
                      registergericht: registergericht.trim(),
                  });
            setPersonen(d);
        } catch (e) {
            await fehlerMelden("Handelsregister", e);
        } finally {
            setLaeuft(null);
            if (contactId) void ladeStand();
        }
    };

    const personenDaten: RegisterBeteiligteErgebnis | null = personen
        ? {
              ...personen,
              // Der mitgebrachte Abdruck ist der Beleg — „Abdruck ansehen" im Popup.
              dokument: personen.abdruck
                  ? {
                        documentId: personen.abdruck.documentId,
                        fileName: "Abdruck",
                        mimeType: personen.abdruck.mimeType,
                    }
                  : personen.dokument ?? null,
          }
        : null;

    const titelMit = (k: Knopf, normal: string) => sperrgrund(k) ?? normal;

    const ad = stand?.aktuell ?? null;
    const cd = stand?.chronologisch ?? null;
    const vorgemerkt = eintraege.length;

    return (
        <div className={s.leiste} ref={leisteRef}>
            {/* Tooltips am Wrapper: ein gesperrter Knopf zeigt seinen eigenen
                `title` nicht in jedem Browser. */}
            <span
                className={s.anker}
                title={titelMit(
                    "personen",
                    vorgemerkt
                        ? `${vorgemerkt} vorgemerkt — werden beim Speichern angelegt`
                        : "Geschäftsführer, Vorstand und Gesellschafter aus dem Handelsregister übernehmen",
                )}
            >
                <button
                    type="button"
                    className={s.knopf}
                    disabled={!!sperrgrund("personen")}
                    onClick={() => void personenHolen("personen")}
                >
                    {laeuft === "personen"
                        ? "Register wird gelesen …"
                        : vorgemerkt
                          ? `Personen aus dem Register (${vorgemerkt} vorgemerkt)`
                          : "Personen aus dem Register"}
                </button>
            </span>

            <span
                className={s.anker}
                // Mit Abdruck trägt der AnsichtsKnopf seinen eigenen Tooltip.
                title={ad ? undefined : titelMit("ad", "Holt den aktuellen Abdruck und die eingetragenen Personen (ein Registerabruf)")}
            >
                {ad ? (
                    <AnsichtsKnopf
                        label="Aktueller Abdruck"
                        title={sperrgrundAnsicht() ?? `Abgerufen am ${tagVon(ad.abgerufenAm) ?? "—"}`}
                        className={mergeClasses(
                            s.knopf,
                            menue === "ad" && s.knopfOffen,
                            !!sperrgrundAnsicht() && s.knopfAus,
                        )}
                        deaktiviert={!!sperrgrundAnsicht()}
                        onClick={() => setMenue(menue === "ad" ? null : "ad")}
                    >
                        {laeuft === "ad" ? "Abdruck wird geholt …" : "Aktueller Abdruck"}
                        {laeuft !== "ad" && <span className={s.pfeil}>▾</span>}
                    </AnsichtsKnopf>
                ) : (
                    <button
                        type="button"
                        className={s.knopf}
                        disabled={!!sperrgrund("ad")}
                        onClick={() => void personenHolen("ad")}
                    >
                        {laeuft === "ad" ? "Abdruck wird geholt …" : "Aktuellen Abdruck holen"}
                    </button>
                )}
                {menue === "ad" && ad && (
                    <div className={s.menue}>
                        <div className={s.menueStand}>Abgerufen am {tagVon(ad.abgerufenAm) ?? "—"}</div>
                        <AnsichtsKnopf label="Öffnen" className={s.menuePunkt} onClick={() => oeffnen(ad)}>
                            Öffnen
                        </AnsichtsKnopf>
                        <AnsichtsKnopf
                            label="Neu herunterladen"
                            title={sperrgrund("ad") ?? "Holt Abdruck und Personen neu (ein Registerabruf)"}
                            className={mergeClasses(s.menuePunkt, !!sperrgrund("ad") && s.menuePunktAus)}
                            deaktiviert={!!sperrgrund("ad")}
                            onClick={() => void personenHolen("ad")}
                        >
                            Neu herunterladen
                        </AnsichtsKnopf>
                    </div>
                )}
            </span>

            <span
                className={s.anker}
                // Mit Abdruck trägt der AnsichtsKnopf seinen eigenen Tooltip.
                title={cd ? undefined : titelMit("cd", "Holt den chronologischen Abdruck — Gründungsdatum und Rechtsvorgänger")}
            >
                {cd ? (
                    <AnsichtsKnopf
                        label="Chronologischer Abdruck"
                        title={sperrgrundAnsicht() ?? `Abgerufen am ${tagVon(cd.abgerufenAm) ?? "—"}`}
                        className={mergeClasses(
                            s.knopf,
                            menue === "cd" && s.knopfOffen,
                            !!sperrgrundAnsicht() && s.knopfAus,
                        )}
                        deaktiviert={!!sperrgrundAnsicht()}
                        onClick={() => setMenue(menue === "cd" ? null : "cd")}
                    >
                        Chronologischer Abdruck
                        <span className={s.pfeil}>▾</span>
                    </AnsichtsKnopf>
                ) : (
                    <button
                        type="button"
                        className={s.knopf}
                        disabled={!!sperrgrund("cd")}
                        onClick={() => setHistorie({})}
                    >
                        Chronologischen Abdruck holen
                    </button>
                )}
                {menue === "cd" && cd && (
                    <div className={s.menue}>
                        <div className={s.menueStand}>Abgerufen am {tagVon(cd.abgerufenAm) ?? "—"}</div>
                        <AnsichtsKnopf
                            label="Öffnen"
                            className={s.menuePunkt}
                            onClick={() => {
                                setMenue(null);
                                setHistorie({ documentId: cd.documentId });
                            }}
                        >
                            Öffnen
                        </AnsichtsKnopf>
                        <AnsichtsKnopf
                            label="Neu herunterladen"
                            title={sperrgrund("cd") ?? "Holt den chronologischen Abdruck neu (ein Registerabruf)"}
                            className={mergeClasses(s.menuePunkt, !!sperrgrund("cd") && s.menuePunktAus)}
                            deaktiviert={!!sperrgrund("cd")}
                            onClick={() => {
                                setMenue(null);
                                setHistorie({});
                            }}
                        >
                            Neu herunterladen
                        </AnsichtsKnopf>
                    </div>
                )}
            </span>

            {personenDaten && (
                <RegisterBeteiligteDialog
                    daten={personenDaten}
                    onClose={() => setPersonen(null)}
                    onEintraege={(e) => onEintraege(e)}
                    onDokumentOeffnen={
                        personen?.abdruck
                            ? () =>
                                  oeffnen({
                                      documentId: personen.abdruck!.documentId,
                                      fileName: personen.abdruck!.fileName,
                                      mimeType: personen.abdruck!.mimeType,
                                      abgerufenAm: personen.abdruck!.abgerufenAm,
                                  })
                            : undefined
                    }
                />
            )}
            {historie && contactId && (
                <RegisterhistorieDialog
                    contactId={contactId}
                    documentId={historie.documentId}
                    nurAnsehen={gesperrt}
                    onClose={() => {
                        setHistorie(null);
                        void ladeStand();
                    }}
                    onUebernehmen={onFelder}
                    onDokumentOeffnen={(d) =>
                        oeffnen({ documentId: d.documentId, fileName: d.fileName, mimeType: d.mimeType, abgerufenAm: null })
                    }
                />
            )}
        </div>
    );
}
